import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AboutNav from '../../Components/Navigation';
import '../../Assets/Styles/AddNew_blog.css';

const AddNew_blog = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    category: 'Market Analysis',
    content: '',
    excerpt: '',
    featuredImage: '',
    tags: '',
    seoTitle: '',
    seoDescription: '',
    status: 'Draft'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // For preview purposes
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Store the file for later upload
      setImageFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(false);
    
    // Validate required fields
    if (!formData.title || !formData.author || !formData.content) {
      setError('Title, author, and content are required fields');
      setIsSubmitting(false);
      return;
    }
    
    // Check if image is selected
    if (!imageFile) {
      setError('Featured image is required');
      setIsSubmitting(false);
      return;
    }
    
    try {
      // First, upload the image to get a URL
      const imageFormData = new FormData();
      imageFormData.append('image', imageFile);
      
      // Upload image first
      const imageResponse = await fetch('https://tstock.trillionstechsolutions.com/blogs-edits/upload-image', {
        method: 'POST',
        body: imageFormData
      });
      
      if (!imageResponse.ok) {
        const errorData = await imageResponse.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }
      
      const imageData = await imageResponse.json();
      const imageUrl = imageData.imageUrl; // Get the URL from the response
      
      // Prepare the blog data in JSON format
      const blogData = {
        title: formData.title,
        author: formData.author,
        category: formData.category,
        content: formData.content,
        excerpt: formData.excerpt,
        featuredImage: imageUrl, // Use the URL returned from the image upload
        tags: formData.tags,
        seoTitle: formData.seoTitle,
        seoDescription: formData.seoDescription,
        status: formData.status
      };
      
      // Send request to API endpoint with JSON data
      const response = await fetch('https://tstock.trillionstechsolutions.com/blogs-edits/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(blogData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create blog post');
      }
      
      const data = await response.json();
      console.log('Blog post created successfully:', data);
      
      setSuccess(true);
      
      // Navigate to blogs page after successful submission
      setTimeout(() => {
        navigate('/blogs');
      }, 1500);
      
    } catch (error) {
      console.error('Error submitting blog post:', error);
      setError(error.message || 'An error occurred while creating the blog post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  return (
    <div className="add-blog-container">
      <AboutNav />
      <div className="add-blog-content">
        <div className="add-blog-header">
          <h1>Create New Blog Post</h1>
          <p>Fill in the details below to create a new blog post</p>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        {success && (
          <div className="success-message">
            Blog post created successfully! Redirecting...
          </div>
        )}
        
        <form className="add-blog-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Basic Information</h2>
            <div className="form-group">
              <label htmlFor="title">Title <span className="required">*</span></label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter blog post title"
                required
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="author">Author <span className="required">*</span></label>
                <input
                  type="text"
                  id="author"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  placeholder="Enter author name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                >
                  <option value="Market Analysis">Market Analysis</option>
                  <option value="Investment">Investment</option>
                  <option value="Risk Management">Risk Management</option>
                  <option value="Cryptocurrency">Cryptocurrency</option>
                  <option value="Personal Finance">Personal Finance</option>
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="Draft">Draft</option>
                <option value="Published">Published</option>
              </select>
            </div>
          </div>
          
          <div className="form-section">
            <h2>Content</h2>
            <div className="form-group">
              <label htmlFor="content">Content <span className="required">*</span></label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="Write your blog post content here..."
                rows="10"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="excerpt">Excerpt</label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                placeholder="A brief summary of your blog post (will be displayed in previews)"
                rows="3"
              />
            </div>
          </div>
          
          <div className="form-section">
            <h2>Media & SEO</h2>
            <div className="form-group">
              <label htmlFor="featuredImage">Featured Image <span className="required">*</span></label>
              <div className="image-upload-container">
                <input
                  type="file"
                  id="featuredImage"
                  name="featuredImage"
                  onChange={handleImageChange}
                  accept="image/*"
                  className="image-input"
                  required
                />
                <div className="image-preview">
                  {previewImage ? (
                    <img src={previewImage} alt="Preview" />
                  ) : (
                    <div className="image-placeholder">
                      <span>No image selected</span>
                      <span className="upload-icon">📷</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="tags">Tags</label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="Enter tags separated by commas (e.g., finance, investment, market)"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="seoTitle">SEO Title</label>
              <input
                type="text"
                id="seoTitle"
                name="seoTitle"
                value={formData.seoTitle}
                onChange={handleChange}
                placeholder="SEO-friendly title (optional)"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="seoDescription">SEO Description</label>
              <textarea
                id="seoDescription"
                name="seoDescription"
                value={formData.seoDescription}
                onChange={handleChange}
                placeholder="SEO-friendly description (optional)"
                rows="2"
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-btn"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Publishing...' : 'Publish Blog Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNew_blog;
